/**
 * Authentication Context
 * Manages user authentication state and operations
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import analytics from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper function to decode JWT token and get expiration
const getTokenExpiration = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch (error) {
    return null;
  }
};

// Helper function to check if token is expiring soon (within 7 days)
const isTokenExpiringSoon = (expiration) => {
  if (!expiration) return false;
  const sevenDays = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  return expiration - Date.now() < sevenDays;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    analytics.logout();
    toast.info('Logged out successfully');
  }, []);

  // Refresh token
  const refreshToken = useCallback(async () => {
    try {
      const response = await api.post('/auth/refresh');
      const { token: newToken } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      logout();
      return null;
    }
  }, [logout]);

  // Load user data
  const loadUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Load user on mount if token exists
  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token, loadUser]);

  // Check token expiration and refresh if needed
  useEffect(() => {
    if (!token) return;

    const expiration = getTokenExpiration(token);
    
    if (!expiration) {
      // Invalid token format
      logout();
      return;
    }

    // Check if token is expired
    if (expiration < Date.now()) {
      toast.error('Your session has expired. Please log in again.');
      logout();
      return;
    }

    // Check if token is expiring soon and refresh
    if (isTokenExpiringSoon(expiration)) {
      // Refresh token silently
      refreshToken().then((newToken) => {
        if (newToken) {
          toast.info('Your session has been refreshed');
        }
      });
    }

    // Set up interval to check token expiration every hour
    const checkInterval = setInterval(() => {
      const currentExpiration = getTokenExpiration(localStorage.getItem('token'));
      if (currentExpiration && currentExpiration < Date.now()) {
        toast.error('Your session has expired. Please log in again.');
        logout();
      } else if (currentExpiration && isTokenExpiringSoon(currentExpiration)) {
        refreshToken();
      }
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(checkInterval);
  }, [token, logout, refreshToken]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        return {
          requiresTwoFactor: true,
          tempToken: response.data.tempToken,
          userId: response.data.user.id
        };
      }

      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      analytics.login('email');
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Verify 2FA code
  const verify2FA = async (tempToken, code) => {
    try {
      const response = await api.post('/auth/2fa/validate', { token: tempToken, code });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      analytics.login('2fa');
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || '2FA verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Register
  const register = async (name, email, password) => {
    try {
      const response = await api.post('/auth/register', { name, email, password });
      
      // Registration now returns pending status, not a token
      analytics.signup();
      toast.success(response.data.message || 'Registration successful! Awaiting approval.');
      return { success: true, pending: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Update failed';
      toast.error(message);
      return false;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    verify2FA,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
